<script setup lang="ts">
import { computed, toRef, watch, type PropType } from "vue";
import { storeToRefs } from "pinia";
import { Trash2, Plus, Settings } from "lucide-vue-next";
import { useSurveyLocalesStore } from "@stores/survey/surveyLocales";
import { useCardsStore } from "@stores/survey/cards";
import { useResultsV2CardsStore } from "@stores/survey/resultsV2/resultsV2.cards";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/episto-ui/select";
import { Switch } from "@/components/ui/switch";
import { POPULATION_AUTO_COLORS } from "@/lib/chartPalettes";
import { removeHtmlMarkdownAndLineBreak } from "@utils/toolBox";
import { CUSTOM_BASE_ELIGIBLE_TYPES } from "@utils/composables/useExportQuestionOptions";
import { useCardLocalOptions } from "@/composables/useCardLocalOptions";
import { useSubtotals } from "@/composables/useSubtotals";
import { rankingPositions } from "@/composables/useChartRules";
import { CHART_TYPES } from "./constants";
import { isCodificationCard, isOpenRecodeCard } from "@utils/hiddenCardQuestionType";
import { TABLE_TILE, extraPopulationTokens, hasChartGrid } from "./cardOptionRules";
import { useCardData } from "@/composables/useCardData";
import { useChartTile } from "@/composables/useChartTile";
import { useResultsV2PopulationsStore } from "@stores/survey/resultsV2/resultsV2.populations";
import { useResultsV2RecodesStore } from "@stores/survey/resultsV2/resultsV2.recodes";
import ColorToggleField from "./CardOptionFields/ColorToggleField.vue";
import ColorsField from "./CardOptionFields/ColorsField.vue";
import CommentField from "./CardOptionFields/CommentField.vue";
import CustomNameTitleField from "./CardOptionFields/CustomNameTitleField.vue";
import OptionRow from "./CardOptionFields/OptionRow.vue";
import OptionSection from "./CardOptionFields/OptionSection.vue";
import PopulationsField from "./CardOptionFields/PopulationsField.vue";
import RadarScaleField from "./CardOptionFields/RadarScaleField.vue";
import type { ChartCard, ResultsCard, ResultsView, ViewPopulation } from "@/types";

const props = defineProps({
  card: { type: Object as PropType<ResultsCard>, required: true },
});

const cardRef = toRef(props, "card");
const { store, localOptions, updateLocalOption } = useCardLocalOptions(cardRef);
const { questionMeta, singlePopulation, matrixPopulations: displayedPopulations } = useCardData(cardRef);
const { tile, chartType } = useChartTile(cardRef, questionMeta, displayedPopulations);
const { availableCards: storeCards, currentView: storeView } = storeToRefs(store);
const availableCards = computed(() => storeCards.value as ChartCard[]);
const currentView = computed(() => storeView.value as ResultsView | null);
const cardsStore = useResultsV2CardsStore();

let refreshTimer: ReturnType<typeof setTimeout> | undefined;
function debouncedRefresh() {
  clearTimeout(refreshTimer);
  refreshTimer = setTimeout(() => store.refreshChartsData(), 500);
}

const localesStore = useSurveyLocalesStore();
const { analysisLocale } = storeToRefs(localesStore);

const cardMeta = computed(() =>
  availableCards.value.find((c) => c.id === props.card.card_id),
);

const displayLocale = computed(() => currentView.value?.options?.locale_token || analysisLocale.value?.token || null);

const { populations: storePopulations } = storeToRefs(useResultsV2PopulationsStore());
const viewPopulations = computed(() => storePopulations.value as ViewPopulation[]);

const hasMultiplePopulations = computed(() => displayedPopulations.value.length > 1);

const isOpenQuestion = computed(() =>
  ["q_open", "q_open_multiple"].includes(cardMeta.value?.type || ""),
);

const withChart = computed(() => hasChartGrid(cardMeta.value));
const isTable = computed(() => withChart.value && tile.value === TABLE_TILE);

const isCodification = computed(() => isCodificationCard(cardMeta.value));
const recodeToken = computed(() => store.recodeTokenForViewCard(props.card));
const isProgrammingRecode = computed(() => {
  const recodeCard = recodeToken.value ? useCardsStore().findByToken(recodeToken.value) : null;
  return !!recodeCard && recodeCard.is_for_analytics === false;
});
const showRecodeSetup = computed(() => (isCodification.value || props.card.card_type === "recode") && !!recodeToken.value);
const configuring = computed(() => cardsStore.isConfiguring(props.card.id));
const recodesStore = useResultsV2RecodesStore();
const computingRecode = computed(() => !!recodeToken.value && !!(recodesStore.loadingRecodes as Record<string, boolean>)[recodeToken.value]);

function openRecodeSetup() {
  cardsStore.openConfig(props.card.id);
}

// --- Base question ---

const customBaseOptions = computed(() =>
  availableCards.value
    .flatMap((c) => {
      const id = c.questions?.[0]?.id;
      return CUSTOM_BASE_ELIGIBLE_TYPES.includes(c.type || "") && c.id !== props.card.card_id && id != null
        ? [{ value: id, label: c.name || c.token }]
        : [];
    }),
);

const customBase = computed({
  get: () => localOptions.value.custom_base || null,
  set: (val: number | null) => updateLocalOption("custom_base", val || null),
});

watch(customBase, () => {
  debouncedRefresh();
});

const ALL_POSITIONS = "all";

const showRankingFilter = computed(
  () =>
    cardMeta.value?.type === "q_ranking" &&
    (isTable.value || chartType.value !== CHART_TYPES.RANKING_FLOW),
);

const rankingFilterOptions = computed(() => {
  const positions = rankingPositions(cardMeta.value);
  return [
    { value: ALL_POSITIONS, label: "All positions" },
    ...positions.slice(0, -1).map((_, i) => ({ value: String(i + 1), label: `Top ${i + 1}` })),
  ];
});

const rankingTopN = computed({
  get: () => (localOptions.value.ranking_top_n ? String(localOptions.value.ranking_top_n) : ALL_POSITIONS),
  set: (val: string) => updateLocalOption("ranking_top_n", val === ALL_POSITIONS ? null : Number(val)),
});

// --- Subtotals ---

const {
  isSubtotalsEligible,
  hasSubtotals,
  subtotalsList,
  subtotalAnswerOptions,
  updateSubtotal,
  removeSubtotal,
} = useSubtotals(cardMeta, localOptions, updateLocalOption, displayLocale, chartType);

const shownSubtotals = computed<{ label: string; codes: string[] }[]>(() => (hasSubtotals.value ? subtotalsList.value : []));

function addSubtotal() {
  if (!hasSubtotals.value) {
    hasSubtotals.value = true;
    return;
  }
  subtotalsList.value = [...subtotalsList.value, { label: "", codes: [] }];
}

function dropSubtotal(index: number) {
  if (subtotalsList.value.length <= 1) {
    hasSubtotals.value = false;
    subtotalsList.value = [];
    return;
  }
  removeSubtotal(index);
}

const isNpsChartType = computed(() => chartType.value === CHART_TYPES.NPS);
const isHeatmap = computed(() => !isTable.value && chartType.value === CHART_TYPES.HEATMAP);
const isRadar = computed(() => !isTable.value && chartType.value === CHART_TYPES.RADAR);
const isDotPlot = computed(() => !isTable.value && chartType.value === CHART_TYPES.DOT_PLOT);

const heatColor = computed({
  get: () => localOptions.value.color || null,
  set: (val: string | null) => updateLocalOption("color", val),
});

const showColors = computed(
  () =>
    withChart.value &&
    !hasMultiplePopulations.value &&
    !isTable.value &&
    !isHeatmap.value &&
    !isRadar.value &&
    !isDotPlot.value,
);

// --- Custom name/title (placeholders) ---

const defaultCardName = computed(() => cardMeta.value?.name || cardMeta.value?.token || "");
const defaultCardTitle = computed(() => {
  const t = cardMeta.value?.specs?.question;
  if (!t) return "";
  const raw = (displayLocale.value && t[displayLocale.value]) || Object.values(t)[0] || "";
  return removeHtmlMarkdownAndLineBreak(raw);
});

function switchOption(key: string, fallback: boolean) {
  return computed({
    get: () => localOptions.value[key] ?? fallback,
    set: (val: boolean) => updateLocalOption(key, val === fallback ? null : val),
  });
}

// --- Sentiment analysis ---

const showSentimentOption = computed(() => isOpenQuestion.value || isCodification.value);

const includeSentiment = switchOption("include_sentiment", true);

const showSummaryOption = computed(
  () => isCodification.value && !isOpenRecodeCard(cardMeta.value),
);

const showSummary = switchOption("show_summary", true);

// --- Display images (image-choice cards) ---

const isImageChoice = computed(() => ["q_image_choice", "q_image_choice_multiple"].includes(cardMeta.value?.type || ""));

const showImages = switchOption("show_images", false);

// --- Skipped responses ---

const showSkippedOption = computed(
  () =>
    Boolean(cardMeta.value?.specs?.skippable) &&
    !isOpenQuestion.value &&
    props.card.card_type !== "recode" &&
    cardMeta.value?.type !== "q_hidden",
);

const includeSkips = switchOption("include_skips", false);

// --- Show counts (table mode only) ---

const showCounts = switchOption("show_counts", true);

const viewTokens = computed(() => viewPopulations.value.flatMap((p) => (p.search_group?.token ? [p.search_group.token] : [])));

const dataCount = computed(
  () =>
    [
      !isOpenQuestion.value && extraPopulationTokens(localOptions.value.custom_populations, viewTokens.value).length > 0,
      !isOpenQuestion.value && !!localOptions.value.custom_base,
      showRankingFilter.value && !!localOptions.value.ranking_top_n,
      showSkippedOption.value && includeSkips.value,
      isSubtotalsEligible.value && !isOpenQuestion.value && shownSubtotals.value.length > 0,
    ].filter(Boolean).length,
);

const hasDataOptions = computed(() => showRecodeSetup.value || !isOpenQuestion.value || showSkippedOption.value);

const hasDisplayOptions = computed(
  () => isTable.value || showColors.value || isHeatmap.value || isRadar.value || showSentimentOption.value || isImageChoice.value,
);

const displayCount = computed(
  () =>
    [
      isTable.value && !showCounts.value,
      showColors.value && !!localOptions.value.choice_colors,
      isHeatmap.value && !!heatColor.value,
      showSummaryOption.value && !showSummary.value,
      showSentimentOption.value && !includeSentiment.value,
      isImageChoice.value && showImages.value,
    ].filter(Boolean).length,
);
</script>

<template>
  <OptionSection
    v-if="hasDataOptions"
    id="data"
    title="Data"
    :count="dataCount">
    <OptionRow
      v-if="showRecodeSetup"
      :label="isCodification ? 'Codification' : 'Recode'"
      :hint="
        isCodification
          ? 'The instructions and categories the AI uses to code the verbatims. Editing happens on the card and recomputes the codification.'
          : 'The rules of the recode. Editing happens on the card and recomputes it.'
      ">
      <Button
        variant="outline"
        size="sm"
        class="w-full"
        :disabled="configuring || computingRecode || isProgrammingRecode"
        @click="openRecodeSetup">
        <Settings class="size-3.5" />
        <template v-if="isProgrammingRecode">Edit it from the questionnaire</template>
        <template v-else-if="configuring">Editing on the card</template>
        <template v-else-if="computingRecode">Computing...</template>
        <template v-else>{{ isCodification ? "Edit categories" : "Edit recode" }}</template>
      </Button>
    </OptionRow>

    <PopulationsField
      v-if="!isOpenQuestion"
      :card="card" />

    <OptionRow
      v-if="!isOpenQuestion"
      label="Base question"
      hint="Compute the results only on respondents who gave a specific answer to another question.">
      <Select
        :model-value="customBase"
        :options="customBaseOptions"
        placeholder="None"
        filterable
        clearable
        @update:model-value="customBase = $event" />
    </OptionRow>

    <OptionRow
      v-if="showRankingFilter"
      label="Ranking filter"
      hint="Bars show the share of respondents ranking each answer within the first N positions.">
      <Select
        :model-value="rankingTopN"
        :options="rankingFilterOptions"
        @update:model-value="rankingTopN = String($event)" />
    </OptionRow>

    <OptionRow
      v-if="showSkippedOption"
      label="Skipped responses"
      hint="Include respondents who skipped the question in the base, as a &quot;Skip&quot; bar."
      inline>
      <Switch
        :model-value="includeSkips"
        @update:model-value="includeSkips = $event" />
    </OptionRow>

    <OptionRow
      v-if="isSubtotalsEligible && !isOpenQuestion"
      label="Subtotals"
      hint="Group several answers under one label, e.g. a Top 2 box.">
      <div class="space-y-1.5">
        <div
          v-for="(sub, index) in shownSubtotals"
          :key="index"
          class="flex items-center gap-1.5">
          <Input
            :model-value="sub.label"
            placeholder="Label"
            class="h-8 w-24 shrink-0"
            @update:model-value="updateSubtotal(index, 'label', $event)" />
          <Select
            :model-value="sub.codes"
            :options="subtotalAnswerOptions"
            placeholder="Answers..."
            multiple
            class="min-w-0 flex-1"
            @update:model-value="updateSubtotal(index, 'codes', $event)" />
          <Button
            variant="ghost"
            size="icon"
            class="size-7 shrink-0 text-muted-foreground hover:text-destructive"
            aria-label="Remove subtotal"
            @click="dropSubtotal(index)">
            <Trash2 class="size-3" />
          </Button>
        </div>
        <Button
          variant="ghost"
          size="sm"
          class="-ml-2 h-7 px-2 text-muted-foreground"
          @click="addSubtotal">
          <Plus class="size-3.5" />
          Add subtotal
        </Button>
      </div>
    </OptionRow>
  </OptionSection>

  <OptionSection
    v-if="hasDisplayOptions"
    id="display"
    title="Display"
    :count="displayCount">
    <OptionRow
      v-if="isTable"
      label="Show counts (N)"
      hint="Show the number of respondents next to each percentage."
      inline>
      <Switch
        :model-value="showCounts"
        @update:model-value="showCounts = $event" />
    </OptionRow>

    <ColorsField
      v-if="showColors"
      :card="card"
      :card-meta="cardMeta"
      :display-locale="displayLocale"
      :nps-chart-type="isNpsChartType" />

    <ColorToggleField
      v-if="isHeatmap"
      v-model="heatColor"
      :default-color="singlePopulation?.color || POPULATION_AUTO_COLORS[0]"
      label="Heat color" />

    <RadarScaleField
      v-if="isRadar"
      :card="card"
      :card-meta="cardMeta"
      :display-locale="displayLocale" />

    <OptionRow
      v-if="showSummaryOption"
      label="Show summary"
      hint="Show the AI summary of the verbatims above the chart. It follows the first population of the card."
      inline>
      <Switch
        :model-value="showSummary"
        @update:model-value="showSummary = $event" />
    </OptionRow>

    <OptionRow
      v-if="showSentimentOption"
      label="Sentiment"
      hint="Split each category bar by the tone of its verbatims."
      inline>
      <Switch
        :model-value="includeSentiment"
        @update:model-value="includeSentiment = $event" />
    </OptionRow>

    <OptionRow
      v-if="isImageChoice"
      label="Display images"
      inline>
      <Switch
        :model-value="showImages"
        @update:model-value="showImages = $event" />
    </OptionRow>
  </OptionSection>

  <CommentField :card="card" />

  <CustomNameTitleField
    :card="card"
    :default-name="defaultCardName"
    :default-title="defaultCardTitle" />
</template>
